import { useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { AppSonner as Sonner, AppToaster as Toaster, AppTooltipProvider as TooltipProvider } from "../ui-foundation/adapters/web";
import { AppProvider, useApp } from "@/contexts/AppContext";
import { useLifeCheck } from "@/hooks/useLifeCheck";
import Layout from "@/components/Layout";
import { QuickReminderComposerProvider } from "@/components/QuickReminderComposer";
import WelcomeScreen from "@/components/WelcomeScreen";
import MoneyPage from "./pages/MoneyPage";
import HomePage from "./pages/HomePage";
import HealthPage from "./pages/HealthPage";
import WorkPage from "./pages/WorkPage";
import FamilyPage from "./pages/FamilyPage";
import SettingsPage from "./pages/SettingsPage";
import AuthPage from "./pages/AuthPage";
import LandingPage from "./pages/LandingPage";
import PricingPage from "./pages/PricingPage";
import AdminPage from "./pages/AdminPage";
import AdminContentPage from "./pages/AdminContentPage";
import DocumentsPage from "./pages/DocumentsPage";
import CalendarPage from "./pages/CalendarPage";
import PetsPage from "./pages/PetsPage";
import CommunityPage from "./pages/CommunityPage";
import AboutPage from "./pages/AboutPage";
import PrivacyPage from "./pages/PrivacyPage";
import GdprPage from "./pages/GdprPage";
import TermsPage from "./pages/TermsPage";
import CodeOfConductPage from "./pages/CodeOfConductPage";
import ContactSupportPage from "./pages/ContactSupportPage";
import ModerationGuidelinesPage from "./pages/ModerationGuidelinesPage";
import AdminCommunityMemberPage from "./pages/AdminCommunityMemberPage";
import AdminMembersPage from "./pages/AdminMembersPage";
import NotFound from "./pages/NotFound";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import LifeCheckPage from "./pages/LifeCheckPage";
import ProfilePage from "./pages/ProfilePage";
import VerifyMfaPage from "./pages/VerifyMfaPage";
import SecureAccountPage from "./pages/SecureAccountPage";
import OpenBankingMobileCallbackPage from "./pages/OpenBankingMobileCallbackPage";
import GuidesPage from "./pages/GuidesPage";
import GuideDetailPage from "./pages/GuideDetailPage";
import AppRuntimeMonitor from "./components/AppRuntimeMonitor";
import ProductAnalyticsTracker from "./components/ProductAnalyticsTracker";
import {
  hasDismissedLifeCheckPrompt,
  hasDismissedLifeCheckResurfacePrompt,
} from "@/lib/lifeCheckPrompt";
import { loadWebOnboardingCompleted } from "@/lib/webOnboardingProgress";
import {
  hasPeopleSection,
  resolveAdultingSectionPath,
} from "../ui-foundation/core";
import {
  resolveLifeCheckPromptMode,
  resolveOnboardingProgress,
} from "../ui-foundation/features/onboarding";
import { syncWebSentryUser } from "@/lib/sentry";

const queryClient = new QueryClient();
const LIFE_CHECK_IGNITION_PATHS = new Set(['/', '/landing', '/auth']);

function shouldOfferLifeCheckIgnition(pathname: string) {
  return LIFE_CHECK_IGNITION_PATHS.has(pathname);
}

function AppRoutes() {
  const { user, loading, data, securityLoading, isPrivilegedUser, requiresMfa, requiresPrivilegedMfaSetup } = useApp();
  const { latestLifeCheck, loading: lifeCheckLoading } = useLifeCheck();
  const location = useLocation();
  const [onboardingCompleted, setOnboardingCompleted] = useState(() =>
    loadWebOnboardingCompleted()
  );
  const publicMarketingPage = location.pathname === "/pricing" ? <PricingPage /> : null;

  useEffect(() => {
    syncWebSentryUser(user ? { id: user.id, email: user.email ?? null } : null);
  }, [user?.email, user?.id]);

  useEffect(() => {
    setOnboardingCompleted(loadWebOnboardingCompleted());
  }, [user?.id]);

  if (loading || securityLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <img src="/adulting-logo.png" alt="Adulting" className="h-16 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (publicMarketingPage) {
    return publicMarketingPage;
  }

  // Unauthenticated: show landing page at /, auth page at /auth
  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/landing" element={<Navigate to="/" replace />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/callback" element={<OpenBankingMobileCallbackPage />} />
        <Route path="/money" element={<OpenBankingMobileCallbackPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/gdpr" element={<GdprPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/community-guidelines" element={<CodeOfConductPage />} />
        <Route path="/contact-complaints" element={<ContactSupportPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  const onboardingState = resolveOnboardingProgress({
    completionFlagLoaded: true,
    completionFlag: onboardingCompleted,
    workType: data.profile.workType,
    entrySurface: 'direct',
  });

  if (onboardingState.shouldShowSetup) {
    return <WelcomeScreen userId={user.id} onComplete={() => {
      setOnboardingCompleted(true);
      window.location.reload();
    }} />;
  }

  const hasChildren = data.profile.children.some((child) => child.name.trim().length > 0);
  const sectionContext = {
    workType: data.profile.workType,
    dependantTypes: data.profile.dependantTypes,
    hasChildren,
    partnerName: data.profile.partnerName,
    hasHouseholdShare: Boolean(data.householdShare),
  };
  const showFamily = hasPeopleSection(sectionContext);
  const defaultRoute = resolveAdultingSectionPath({
    preferred: localStorage.getItem('adulting_default_section'),
    lastVisited: localStorage.getItem('adulting_last_section'),
    context: sectionContext,
  });
  const shouldOfferLifeCheckPromptRoute =
    shouldOfferLifeCheckIgnition(location.pathname) || location.pathname === defaultRoute;
  const lifeCheckPromptMode =
    !lifeCheckLoading && shouldOfferLifeCheckPromptRoute && onboardingState.isComplete
      ? resolveLifeCheckPromptMode({
          latestLifeCheckCreatedAt: latestLifeCheck?.createdAt ?? null,
          ignitionPromptDismissed: hasDismissedLifeCheckPrompt(user.id),
          resurfacePromptDismissed: hasDismissedLifeCheckResurfacePrompt(user.id),
        })
      : null;

  if (shouldOfferLifeCheckPromptRoute && onboardingState.isComplete && lifeCheckLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <img src="/adulting-logo.png" alt="Adulting" className="h-16 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Preparing your Life Check…</p>
        </div>
      </div>
    );
  }

  if (lifeCheckPromptMode) {
    return <Navigate to={`/life-check?entry=${lifeCheckPromptMode}`} replace />;
  }
  const renderPrivilegedRoute = (element: JSX.Element) => {
    if (!isPrivilegedUser) {
      return <Navigate to={defaultRoute} replace />;
    }

    if (requiresPrivilegedMfaSetup) {
      return <Navigate to="/secure-account" replace />;
    }

    if (requiresMfa) {
      return <Navigate to="/verify-2fa" replace />;
    }

    return element;
  };

  return (
    <>
      <AppRuntimeMonitor />
      <ProductAnalyticsTracker />
      <QuickReminderComposerProvider>
        <Layout>
          <Routes>
          <Route path="/" element={<Navigate to={defaultRoute} replace />} />
          <Route path="/landing" element={<Navigate to={defaultRoute} replace />} />
          <Route path="/pricing" element={<Navigate to={defaultRoute} replace />} />
          <Route path="/auth" element={<Navigate to={defaultRoute} replace />} />
          <Route
            path="/secure-account"
            element={requiresPrivilegedMfaSetup ? <SecureAccountPage /> : <Navigate to={defaultRoute} replace />}
          />
          <Route
            path="/verify-2fa"
            element={requiresMfa ? <VerifyMfaPage /> : <Navigate to={defaultRoute} replace />}
          />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/callback" element={<OpenBankingMobileCallbackPage />} />
          <Route path="/dashboard" element={<Navigate to={defaultRoute} replace />} />
          <Route path="/money" element={<MoneyPage />} />
          <Route path="/home" element={<HomePage />} />
          <Route path="/health" element={<HealthPage />} />
          <Route path="/work" element={<WorkPage />} />
          <Route path="/family" element={showFamily ? <FamilyPage /> : <Navigate to="/profile" replace />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/guides" element={<GuidesPage />} />
          <Route path="/guides/:section/:kind/:guideId" element={<GuideDetailPage />} />
          <Route path="/life-check" element={<LifeCheckPage />} />
          <Route path="/admin" element={renderPrivilegedRoute(<AdminPage />)} />
          <Route path="/admin/content" element={renderPrivilegedRoute(<AdminContentPage />)} />
          <Route path="/admin/community-members" element={renderPrivilegedRoute(<AdminMembersPage />)} />
          <Route path="/admin/community-members/:userId" element={renderPrivilegedRoute(<AdminCommunityMemberPage />)} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/pets" element={<PetsPage />} />
          <Route path="/community" element={<CommunityPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/gdpr" element={<GdprPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/community-guidelines" element={<CodeOfConductPage />} />
          <Route path="/contact-complaints" element={<ContactSupportPage />} />
          <Route path="/admin/moderation-guidelines" element={renderPrivilegedRoute(<ModerationGuidelinesPage />)} />
          <Route path="*" element={<NotFound />} />
          </Routes>
        </Layout>
      </QuickReminderComposerProvider>
    </>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AppProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AppProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
